import { z } from 'zod';
/**
 * Genealogische Daten sind selten exakt. „um 1890“, „vor dem Krieg“,
 * „zwischen 1912 und 1914“, „März 1945“ – all das muss speicherbar,
 * sortierbar und wieder lesbar sein.
 *
 * Repräsentation: ein Intervall [start, end] plus Qualifier und Präzision.
 * - exact   : start == end, Präzision sagt, wie genau
 * - about   : ~ start, Intervall mit Toleranz aufgeweitet
 * - before  : end gesetzt, start offen
 * - after   : start gesetzt, end offen
 * - between : start und end gesetzt
 * - unknown : nichts gesetzt, evtl. nur Freitext
 */
export const DATE_QUALIFIERS = ['exact', 'about', 'before', 'after', 'between', 'unknown'];
export const DATE_PRECISIONS = ['day', 'month', 'year', 'decade', 'century'];
/** ISO-Datum ohne Zeitanteil: YYYY-MM-DD */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet YYYY-MM-DD')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Kein gültiges Datum');
export const fuzzyDateSchema = z
    .object({
    qualifier: z.enum(DATE_QUALIFIERS).default('exact'),
    precision: z.enum(DATE_PRECISIONS).default('day'),
    /** Untere Grenze des Intervalls (inklusiv). */
    start: isoDate.nullable().default(null),
    /** Obere Grenze des Intervalls (inklusiv). */
    end: isoDate.nullable().default(null),
    /** Originaleingabe, damit nichts verloren geht. */
    text: z.string().trim().max(200).nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.start && v.end && v.start > v.end) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Start liegt nach Ende', path: ['end'] });
    }
    if (v.qualifier === 'between' && (!v.start || !v.end)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '„zwischen“ braucht Start und Ende',
            path: ['qualifier'],
        });
    }
    if (v.qualifier === 'before' && !v.end) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '„vor“ braucht ein Ende', path: ['end'] });
    }
    if (v.qualifier === 'after' && !v.start) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '„nach“ braucht einen Start', path: ['start'] });
    }
});
export const EMPTY_FUZZY_DATE = {
    qualifier: 'unknown',
    precision: 'year',
    start: null,
    end: null,
    text: null,
};
export function isEmptyFuzzyDate(d) {
    return !d || (d.qualifier === 'unknown' && !d.start && !d.end && !d.text);
}
/**
 * Ist die Angabe *unscharf* – „um 1890“, „vor 1945“, „zwischen 1912 und 1914“?
 *
 * Gemeint ist die Einschränkung, nicht die Genauigkeit: „1901“ ohne Tag ist
 * eine feste Angabe, nur eine gröbere. Deshalb entscheidet allein der
 * Qualifizierer.
 *
 * Gebraucht wird das für die Darstellung: in den Listen steht eine unscharfe
 * Angabe in Gold. So sieht man beim Überfliegen einer Spalte, wo das Archiv
 * schätzt und wo es weiß – ohne dass jede Zeile eine Fußnote braucht.
 */
export function isApproximateDate(d) {
    if (!d || isEmptyFuzzyDate(d))
        return false;
    return d.qualifier !== 'exact';
}
/**
 * Gleichheit zweier unscharfer Daten. „Leer“ und `null` sind dasselbe: ein
 * Feld, in dem nie etwas stand, und eines, das man leergetippt hat, sagen
 * beide „unbekannt“. Wird gebraucht, um zu erkennen, ob ein Formular
 * gegenüber dem gespeicherten Stand überhaupt eine Änderung enthält.
 */
export function fuzzyDateEquals(a, b) {
    if (isEmptyFuzzyDate(a) && isEmptyFuzzyDate(b))
        return true;
    if (!a || !b)
        return false;
    return (a.qualifier === b.qualifier &&
        a.precision === b.precision &&
        a.start === b.start &&
        a.end === b.end &&
        (a.text ?? null) === (b.text ?? null));
}
/**
 * Meinen zwei Angaben denselben Zeitraum – unabhängig davon, wie er getippt
 * wurde?
 *
 * Der Unterschied zu `fuzzyDateEquals` ist das Feld `text`. Dort steht die
 * Originaleingabe, damit nichts verloren geht; „19.1.1862“ und „19.01.1862“
 * sind aber derselbe Tag, und „12. März 1901“ ist derselbe wie „12.03.1901“.
 *
 * Beide Vergleiche werden gebraucht, und keiner ersetzt den anderen. Beim
 * Bearbeiten eines Formulars ist eine geänderte Schreibweise eine Änderung,
 * die gespeichert werden will – dort gilt `fuzzyDateEquals`. Beim Abgleich
 * zweier Bestände ist sie keine: dem Import als Widerspruch vorgelegt, ließe
 * sie jemanden zwischen zwei identischen Daten wählen und würde ihn lehren,
 * die Meldung zu überblättern.
 */
export function fuzzyDateEquivalent(a, b) {
    if (isEmptyFuzzyDate(a) && isEmptyFuzzyDate(b))
        return true;
    if (!a || !b)
        return false;
    /**
     * Ohne Intervall trägt allein der Freitext die Aussage – „16.06“ (Tag und
     * Monat ohne Jahr) und „20.04.“ sind beide unlesbar, aber nicht dasselbe.
     */
    if (!a.start && !a.end && !b.start && !b.end)
        return (a.text ?? null) === (b.text ?? null);
    return (a.qualifier === b.qualifier &&
        a.precision === b.precision &&
        a.start === b.start &&
        a.end === b.end);
}
/** Sortierschlüssel: frühestmöglicher Zeitpunkt. Wird als eigene Spalte persistiert. */
export function fuzzyDateSortKey(d) {
    if (!d)
        return null;
    return d.start ?? d.end ?? null;
}
/**
 * Jahreszahl für kompakte Anzeigen. Bei „um 1900“ ist das Intervall bewusst
 * aufgeweitet (1898–1902) – dort ist die Mitte die gemeinte Jahreszahl,
 * nicht der Anfang.
 */
export function fuzzyDateYear(d) {
    if (!d)
        return null;
    if (d.qualifier === 'about' && d.start && d.end) {
        return Math.round((Number(d.start.slice(0, 4)) + Number(d.end.slice(0, 4))) / 2);
    }
    const key = fuzzyDateSortKey(d);
    return key ? Number(key.slice(0, 4)) : null;
}
// ── Parsing ────────────────────────────────────────────────────────────────
const MONTHS_DE = {
    jan: 1, januar: 1,
    feb: 2, februar: 2,
    mrz: 3, mar: 3, maerz: 3, 'märz': 3,
    apr: 4, april: 4,
    mai: 5,
    jun: 6, juni: 6,
    jul: 7, juli: 7,
    aug: 8, august: 8,
    sep: 9, sept: 9, september: 9,
    okt: 10, oct: 10, oktober: 10,
    nov: 11, november: 11,
    dez: 12, dec: 12, dezember: 12,
};
function pad(n, len = 2) {
    return String(n).padStart(len, '0');
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function ymd(year, month, day) {
    return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}
function spanForYear(year) {
    return [ymd(year, 1, 1), ymd(year, 12, 31)];
}
function spanForMonth(year, month) {
    return [ymd(year, month, 1), ymd(year, month, daysInMonth(year, month))];
}
/**
 * Nimmt freie Nutzereingabe und macht daraus einen FuzzyDate.
 * Versteht u.a.: „12.03.1901“, „1901-03-12“, „März 1901“, „03/1901“,
 * „um 1890“, „ca. 1890“, „vor 1945“, „nach 1918“, „1912–1914“, „1880er“.
 * Gibt null zurück, wenn nichts Verwertbares erkannt wurde (Freitext landet in `text`).
 */
export function parseFuzzyDate(input) {
    const raw = input.trim();
    if (!raw)
        return { ...EMPTY_FUZZY_DATE };
    const text = raw;
    let s = raw.toLowerCase().replace(/\s+/g, ' ');
    let qualifier = 'exact';
    // Qualifier-Präfixe abschälen
    const about = /^(um|ca\.?|circa|etwa|about|abt\.?|~)\s*/;
    const before = /^(vor|before|bef\.?|<)\s*/;
    const after = /^(nach|ab|after|aft\.?|>)\s*/;
    if (about.test(s)) {
        qualifier = 'about';
        s = s.replace(about, '');
    }
    else if (before.test(s)) {
        qualifier = 'before';
        s = s.replace(before, '');
    }
    else if (after.test(s)) {
        qualifier = 'after';
        s = s.replace(after, '');
    }
    // Bereich: „1912–1914“, „zwischen 1912 und 1914“
    const between = /^(?:zwischen\s+|between\s+)?(\d{3,4})\s*(?:-|–|—|\/|bis|und|and|to)\s*(\d{3,4})$/.exec(s);
    if (between) {
        const a = Number(between[1]);
        const b = Number(between[2]);
        return {
            qualifier: 'between',
            precision: 'year',
            start: spanForYear(Math.min(a, b))[0],
            end: spanForYear(Math.max(a, b))[1],
            text,
        };
    }
    // Dekade: „1880er“, „1880s“
    const decade = /^(\d{3,4})\s*(?:er|s)$/.exec(s);
    if (decade) {
        const y = Number(decade[1]);
        const base = Math.floor(y / 10) * 10;
        return {
            qualifier: qualifier === 'exact' ? 'about' : qualifier,
            precision: 'decade',
            start: ymd(base, 1, 1),
            end: ymd(base + 9, 12, 31),
            text,
        };
    }
    const finish = (precision, start, end) => {
        if (qualifier === 'before')
            return { qualifier, precision, start: null, end, text };
        if (qualifier === 'after')
            return { qualifier, precision, start, end: null, text };
        if (qualifier === 'about') {
            // Toleranz: ±2 Jahre um das Intervall
            const y0 = Number(start.slice(0, 4)) - 2;
            const y1 = Number(end.slice(0, 4)) + 2;
            return {
                qualifier,
                precision,
                start: ymd(Math.max(1, y0), Number(start.slice(5, 7)), Number(start.slice(8, 10))),
                end: ymd(y1, Number(end.slice(5, 7)), Number(end.slice(8, 10))),
                text,
            };
        }
        return { qualifier: 'exact', precision, start, end, text };
    };
    // ISO: 1901-03-12 / 1901-03 / 1901
    const iso = /^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?$/.exec(s);
    if (iso) {
        const y = Number(iso[1]);
        const m = iso[2] ? Number(iso[2]) : null;
        const d = iso[3] ? Number(iso[3]) : null;
        if (m && d)
            return finish('day', ymd(y, m, d), ymd(y, m, d));
        if (m) {
            const [a, b] = spanForMonth(y, m);
            return finish('month', a, b);
        }
        const [a, b] = spanForYear(y);
        return finish('year', a, b);
    }
    // Deutsch: 12.03.1901 / 3.1901 / 12.3.01 (zweistellig wird nicht geraten)
    const de = /^(\d{1,2})\.(?:(\d{1,2})\.)?(\d{4})$/.exec(s);
    if (de) {
        const first = Number(de[1]);
        const second = de[2] ? Number(de[2]) : null;
        const y = Number(de[3]);
        if (second !== null) {
            if (second >= 1 && second <= 12 && first >= 1 && first <= daysInMonth(y, second)) {
                return finish('day', ymd(y, second, first), ymd(y, second, first));
            }
        }
        else if (first >= 1 && first <= 12) {
            const [a, b] = spanForMonth(y, first);
            return finish('month', a, b);
        }
    }
    // Slash: 03/1901 oder 12/03/1901 (Tag zuerst, europäisch)
    const slash = /^(\d{1,2})\/(?:(\d{1,2})\/)?(\d{4})$/.exec(s);
    if (slash) {
        const first = Number(slash[1]);
        const second = slash[2] ? Number(slash[2]) : null;
        const y = Number(slash[3]);
        if (second !== null && second >= 1 && second <= 12) {
            return finish('day', ymd(y, second, first), ymd(y, second, first));
        }
        if (second === null && first >= 1 && first <= 12) {
            const [a, b] = spanForMonth(y, first);
            return finish('month', a, b);
        }
    }
    // Monatsname: „märz 1901“, „12. märz 1901“
    const named = /^(?:(\d{1,2})\.?\s*)?([a-zäöü]+)\.?\s+(\d{4})$/.exec(s);
    if (named) {
        const monthName = (named[2] ?? '').replace(/\./g, '');
        const m = MONTHS_DE[monthName];
        const y = Number(named[3]);
        if (m) {
            if (named[1]) {
                const d = Number(named[1]);
                if (d >= 1 && d <= daysInMonth(y, m))
                    return finish('day', ymd(y, m, d), ymd(y, m, d));
            }
            const [a, b] = spanForMonth(y, m);
            return finish('month', a, b);
        }
    }
    // Nichts erkannt: als Freitext behalten.
    return { qualifier: 'unknown', precision: 'year', start: null, end: null, text };
}
// ── Formatierung ───────────────────────────────────────────────────────────
const MONTH_NAMES_DE = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
function formatPoint(iso, precision) {
    const y = Number(iso.slice(0, 4));
    const m = Number(iso.slice(5, 7));
    const d = Number(iso.slice(8, 10));
    switch (precision) {
        case 'day':
            return `${pad(d)}.${pad(m)}.${y}`;
        case 'month':
            return `${MONTH_NAMES_DE[m - 1]} ${y}`;
        case 'decade':
            return `${Math.floor(y / 10) * 10}er`;
        case 'century':
            return `${Math.floor(y / 100) + 1}. Jh.`;
        case 'year':
        default:
            return String(y);
    }
}
/** Menschenlesbare Ausgabe, z.B. „um 1890“, „vor 1945“, „1912–1914“, „12.03.1901“. */
export function formatFuzzyDate(d, fallback = '—') {
    if (!d)
        return fallback;
    if (d.qualifier === 'unknown')
        return d.text?.trim() || fallback;
    switch (d.qualifier) {
        case 'before':
            return d.end ? `vor ${formatPoint(d.end, d.precision)}` : (d.text ?? fallback);
        case 'after':
            return d.start ? `nach ${formatPoint(d.start, d.precision)}` : (d.text ?? fallback);
        case 'between': {
            if (!d.start || !d.end)
                return d.text ?? fallback;
            return `${formatPoint(d.start, d.precision)}–${formatPoint(d.end, d.precision)}`;
        }
        case 'about': {
            const anchor = d.start ?? d.end;
            if (!anchor)
                return d.text ?? fallback;
            // Bei „about“ wurde das Intervall aufgeweitet – Mitte zeigen.
            if (d.start && d.end) {
                const mid = Math.round((Number(d.start.slice(0, 4)) + Number(d.end.slice(0, 4))) / 2);
                return d.precision === 'decade'
                    ? `${Math.floor(mid / 10) * 10}er`
                    : `um ${mid}`;
            }
            return `um ${formatPoint(anchor, 'year')}`;
        }
        case 'exact':
        default: {
            const anchor = d.start ?? d.end;
            return anchor ? formatPoint(anchor, d.precision) : (d.text ?? fallback);
        }
    }
}
/** Jahreszahl mit Unschärfe-Markierung: „1901“, „~1900“, „<1945“, „>1918“. */
function yearMark(d) {
    const year = fuzzyDateYear(d);
    if (year === null || !d)
        return null;
    switch (d.qualifier) {
        case 'about':
            return `~${year}`;
        case 'before':
            return `<${year}`;
        case 'after':
            return `>${year}`;
        default:
            return String(year);
    }
}
/** Lebensspanne für Listen: „1901–1978“, „~1900–1983“, „1961–“, „†1978“. */
export function formatLifespan(birth, death) {
    const b = yearMark(birth);
    const d = yearMark(death);
    if (b && d)
        return `${b}–${d}`;
    if (b)
        return `${b}–`;
    if (d)
        return `†${d}`;
    return '—';
}
/** Lebensspanne, wenn nur die reinen Jahreszahlen vorliegen (Graph-Knoten). */
export function formatLifespanYears(birthYear, deathYear) {
    if (birthYear && deathYear)
        return `${birthYear}–${deathYear}`;
    if (birthYear)
        return `${birthYear}–`;
    if (deathYear)
        return `†${deathYear}`;
    return '—';
}
/** Alter in Jahren, sofern beide Grenzen brauchbar sind. */
export function computeAge(birth, death, today = new Date()) {
    const b = fuzzyDateSortKey(birth);
    if (!b)
        return null;
    const end = fuzzyDateSortKey(death) ?? today.toISOString().slice(0, 10);
    const by = Number(b.slice(0, 4));
    const bm = Number(b.slice(5, 7));
    const bd = Number(b.slice(8, 10));
    const ey = Number(end.slice(0, 4));
    const em = Number(end.slice(5, 7));
    const ed = Number(end.slice(8, 10));
    let age = ey - by;
    if (em < bm || (em === bm && ed < bd))
        age -= 1;
    return age >= 0 && age < 130 ? age : null;
}
// ── Aufnahmedatum gegen Lebensdaten ────────────────────────────────────────
/**
 * Jahresspanne, in der ein unscharfes Datum liegen kann; `null` heißt offen.
 *
 * Für die Frage „widerspricht sich das?“ ist genau diese Offenheit der Kern:
 * „vor 1945“ hat keine untere Grenze, „nach 1918“ keine obere. Wer daraus eine
 * einzelne Jahreszahl macht (wie `fuzzyDateYear` für die Anzeige), urteilt über
 * Daten, die das gar nicht hergeben.
 */
export function fuzzyDateYearRange(d) {
    if (!d || d.qualifier === 'unknown')
        return { from: null, to: null };
    const year = (iso) => (iso ? Number(iso.slice(0, 4)) : null);
    switch (d.qualifier) {
        case 'before':
            return { from: null, to: year(d.end) };
        case 'after':
            return { from: year(d.start), to: null };
        default:
            // „um 1890“ ist beim Parsen schon auf 1888–1892 aufgeweitet – die
            // Toleranz steckt also bereits im Intervall.
            return { from: year(d.start) ?? year(d.end), to: year(d.end) ?? year(d.start) };
    }
}
/** Lesbare Spanne für Hinweistexte: „1901“, „1912–1914“, „vor 1945“. */
export function yearSpanLabel(from, to) {
    if (from !== null && to !== null)
        return from === to ? String(from) : `${from}–${to}`;
    if (from !== null)
        return `nach ${from}`;
    if (to !== null)
        return `vor ${to}`;
    return 'ohne Datum';
}
/**
 * Prüft, ob Aufnahmedatum und Lebensdaten sich widersprechen.
 *
 * Gemeldet wird nur, was auch bei unscharfen Daten sicher nicht zusammenpasst:
 * das *späteste* mögliche Aufnahmejahr liegt noch vor der Geburt, oder das
 * *früheste* liegt schon nach dem Tod. „nach 1918“ bei einer 1950 geborenen
 * Person ist damit kein Widerspruch – das Bild kann von 1955 sein.
 *
 * Verglichen werden Jahreszahlen, weil eine `PersonRef` nur die trägt. Ein
 * Widerspruch von einem Jahr ist deshalb ebenso wenig belastbar wie das
 * zugrunde liegende Datum; die Meldung sagt darum nicht „falsch“, sondern
 * benennt beide Zahlen und lässt offen, welche davon stimmt.
 */
export function photoDateConflict(person, takenAt) {
    const { from, to } = fuzzyDateYearRange(takenAt);
    const shot = yearSpanLabel(from, to);
    if (to !== null && person.birthYear !== null && to < person.birthYear) {
        const gap = person.birthYear - to;
        return {
            kind: 'before-birth',
            label: 'Aufnahme liegt vor der Geburt',
            detail: `Aufnahme ${shot}, geboren ${person.birthYear} – das Bild ist ` +
                `${gap} ${gap === 1 ? 'Jahr' : 'Jahre'} älter als die Person. ` +
                `Vermutlich stimmt eines der beiden Jahre nicht.`,
        };
    }
    if (from !== null && person.deathYear !== null && from > person.deathYear) {
        return {
            kind: 'after-death',
            label: `Aufnahme nach dem Tod (†${person.deathYear})`,
            detail: `Aufnahme ${shot}, gestorben ${person.deathYear} – das Bild kann die ` +
                `Person nicht zeigen (oder ein Jahr stimmt nicht).`,
        };
    }
    return null;
}
/**
 * Alle Widersprüche auf einem Bild. Nimmt die Personenliste, wie sie in der
 * Oberfläche vorliegt – `null`-Einträge (unbenannte Markierungen) und
 * Doppelungen (zwei Markierungen derselben Person) fallen heraus.
 */
export function photoDateConflicts(persons, takenAt) {
    const found = [];
    const seen = new Set();
    for (const person of persons) {
        if (!person || seen.has(person.id))
            continue;
        seen.add(person.id);
        const conflict = photoDateConflict(person, takenAt);
        if (conflict)
            found.push({ person, conflict });
    }
    return found;
}
export function photoAge(person, takenAt) {
    const takenYear = fuzzyDateYear(takenAt);
    if (takenYear === null || person.birthYear === null)
        return null;
    const conflict = photoDateConflict(person, takenAt);
    const years = takenYear - person.birthYear;
    const basis = `geboren ${person.birthYear}, Aufnahme ${takenYear}`;
    if (years < 0) {
        // Kein Widerspruch trotz negativer Zahl? Dann ist das Bild nur nach oben
        // offen datiert („nach 1918“) und die Jahreszahl ist bloß die untere
        // Grenze – daraus lässt sich kein Alter machen, also gibt es keins.
        if (!conflict)
            return null;
        return {
            years,
            short: '?',
            label: 'vor der Geburt?',
            hint: conflict.detail,
            warning: conflict.label,
            conflict,
        };
    }
    const label = years === 0 ? 'unter 1 Jahr' : years === 1 ? '1 Jahr' : `${years} Jahre`;
    // „· 0“ am Gesicht liest sich wie ein Fehler; „<1“ sagt dasselbe verständlich.
    const short = years === 0 ? '<1' : String(years);
    return {
        years,
        short,
        label,
        hint: conflict
            ? conflict.detail
            : `Alter im Bild: ${label} (${basis}, auf etwa ein Jahr genau).`,
        warning: conflict?.label ?? null,
        conflict,
    };
}
//# sourceMappingURL=fuzzy-date.js.map